<template lang="pug">
teleport(to="body")
  .cause-dialog-backdrop(v-if="modelValue" @click="handleBackdropClick")
    .cause-dialog(@click.stop)
      .dialog-header
        h2.dialog-title {{ dialogTitle }}
        button.close-btn(type="button" @click="handleClose" aria-label="Close")
          img(src="@/assets/icons/close.svg" alt="")

      p.dialog-subtitle(v-if="formRows.length > 1")
        | Choose a charity for each item before adding to your cart.

      .item-forms
        .item-form(v-for="row in formRows" :key="row.sticker.id")
          .item-header
            img.item-thumb(v-if="row.sticker.url" :src="row.sticker.url" :alt="row.sticker.title || 'Sticker'")
            .item-meta
              .item-title {{ row.sticker.title || 'Sticker' }}
              .item-amount-note(v-if="row.amount > 0")
                | Donation: {{ formatCurrency(row.amount) }}
                span(v-if="row.quantity > 1")  × {{ row.quantity }}

          .charity-section-label Creator's Suggestions
          .charity-suggestions
            el-checkbox-group(v-model="row.selectedCauseIds").charity-checkbox-group
              div(
                v-for="(cause, index) in row.causes"
                :key="`${row.sticker.id}-${index}`"
              )
                div(v-if="cause").charity-row
                  el-checkbox(
                    :label="getCauseId(cause)"
                  ).charity-suggestion
                    | {{ getCauseName(cause) }}
                hr(v-if="index < row.causes.length - 1 && cause").hr-color

          .cause-selection-note(v-if="row.selectedCauseIds.length > 1")
            | Each charity will receive {{ formatCurrency(row.amount) }}
            br
            | Total charge: {{ formatCurrency(row.amount * row.selectedCauseIds.length) }}

          .error-message(v-if="row.error") {{ row.error }}

      .action-buttons
        button.action-btn.primary-btn(
          type="button"
          :disabled="!canSubmit"
          @click="handleSubmit"
        ) Add to cart
        button.action-btn.secondary-btn(type="button" @click="handleClose") Cancel
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, PropType,
} from 'vue';
import {
  CartCauseDialogItem,
  getCauseDisplayName,
  getCauseIdFromOption,
  getStickerCauseOptions,
} from '@/utils/cartItemAmount';

interface FormRow {
  sticker: any;
  causes: string[];
  amount: number;
  quantity: number;
  selectedCauseIds: string[];
  error: string;
}

export default defineComponent({
  name: 'DialogCartChooseCause',
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    items: {
      type: Array as PropType<CartCauseDialogItem[]>,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'confirm'],
  setup(props, { emit }) {
    const formRows = ref<FormRow[]>([]);

    const dialogTitle = computed(() => (
      props.title || (props.items.length > 1 ? 'Choose charities' : 'Choose a charity')
    ));

    function formatCurrency(value: number): string {
      return `$${Number(value || 0).toFixed(2)}`;
    }

    function getCauseName(cause: string): string {
      return getCauseDisplayName(cause);
    }

    function getCauseId(cause: string): string {
      return getCauseIdFromOption(cause);
    }

    function buildFormRows(items: CartCauseDialogItem[]): FormRow[] {
      return items.map((item) => ({
        sticker: item.sticker,
        causes: getStickerCauseOptions(item.sticker),
        amount: item.amount,
        quantity: item.quantity,
        selectedCauseIds: [],
        error: '',
      }));
    }

    watch(
      () => [props.modelValue, props.items] as const,
      ([visible, items]) => {
        if (visible && items?.length) {
          formRows.value = buildFormRows(items);
        }
      },
      { immediate: true, deep: true },
    );

    const canSubmit = computed(() => (
      formRows.value.length > 0
      && formRows.value.every((row) => row.selectedCauseIds.length > 0 && !row.error)
    ));

    function handleClose() {
      emit('update:modelValue', false);
    }

    function handleBackdropClick() {
      handleClose();
    }

    function handleSubmit() {
      formRows.value = formRows.value.map((row) => ({
        ...row,
        error: row.selectedCauseIds.length === 0 ? 'Select at least one charity' : '',
      }));

      if (!canSubmit.value) return;

      const selections: Record<string, string[]> = {};
      formRows.value.forEach((row) => {
        selections[row.sticker.id] = [...row.selectedCauseIds];
      });

      emit('confirm', selections);
      emit('update:modelValue', false);
    }

    return {
      formRows,
      dialogTitle,
      canSubmit,
      formatCurrency,
      getCauseName,
      getCauseId,
      handleClose,
      handleBackdropClick,
      handleSubmit,
    };
  },
});
</script>

<style lang="scss" scoped>
.cause-dialog-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

.cause-dialog {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  max-width: 480px;
  width: 92%;
  max-height: 90vh;
  overflow-y: auto;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.dialog-title {
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0;
  flex: 1;
  padding-right: 1rem;
}

.dialog-subtitle {
  margin: 0 0 16px;
  color: #666;
  font-size: 0.9rem;
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f5f5f5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-forms {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.item-form {
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 16px;
}

.item-header {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.item-thumb {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
}

.item-title {
  font-weight: 600;
  font-size: 1rem;
}

.item-amount-note {
  font-size: 0.85rem;
  color: #666;
  margin-top: 4px;
}

.charity-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #9ca3af;
  margin-bottom: 4px;
}

.charity-suggestions {
  display: flex;
  flex-direction: column;
  width: 100%;

  .charity-checkbox-group {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .charity-row {
    padding: 10px 0;
  }

  .charity-suggestion {
    width: 100%;

    :deep(.el-checkbox__label) {
      white-space: normal;
      word-break: break-word;
      font-size: 15px;
      font-weight: 500;
      color: #1a1a1a;
      line-height: 1.4;
    }

    :deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
      color: #0d9488;
      font-weight: 600;
    }

    :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
      background: #0d9488;
      border-color: #0d9488;
    }
  }

  .hr-color {
    border: none;
    border-top: 1px solid #f3f4f6;
    margin: 0;
    width: 100%;
  }
}

.cause-selection-note {
  margin-top: 12px;
  padding: 10px 12px;
  background: #f0fdfa;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #0d9488;
  line-height: 1.5;
}

.error-message {
  margin-top: 8px;
  color: #d93025;
  font-size: 0.85rem;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.action-btn {
  border: none;
  border-radius: 10px;
  padding: 14px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}

.primary-btn {
  background: #0d9488;
  color: #fff;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.secondary-btn {
  background: #f5f5f5;
  color: #333;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
